import Vue from 'vue';
import axios from 'axios';
import jwt from 'jsonwebtoken';

export default {
  namespaced: true,
  state: () => ({
    step: 0,
    loading: false,
    // user is the response from the login endpoint, contains token, mobile, email, security questions
    user: null,
    error: null,
    reset: null,
    questions: null,
    resetSuccess: false,
    bearerToken: null,
    userTokenStorageKey: '__3dn_acc_tok',
    eventUser: null,

    // store here for easy access downstream
    parsedUrlParams: null,
    authConfig: null,

    // note: not actually using this yet, but leaving it in in case it's useful
    eventUserSetBy: null,
  }),
  mutations: {
    setStep(state, payload) {
      state.step = payload;
    },
    setLoading(state, payload) {
      state.loading = payload;
    },
    setAuthConfig(state, payload) {
      state.authConfig = payload;
    },
    setUser(state, payload) {
      state.user = payload;
    },
    setError(state, payload) {
      state.error = payload;
    },
    setReset(state, payload) {
      state.reset = payload;
    },
    setQuestions(state, payload) {
      state.questions = payload;
    },
    setResetSuccess(state, payload) {
      state.resetSuccess = payload;
    },
    setBearerToken(state, payload) {
      state.bearerToken = payload
    },
    setEventUser(state, payload) {
      state.eventUser = payload
    },
    setEventUserSetBy(state, payload) {
      state.eventUserSetBy = payload
    },
    setUrlParams(state, payload) {
      state.parsedUrlParams = payload
    }
  },
  actions: {
    async configure({commit}) {
      // const domain = 'https://xperience.3degreesnorth.com.au/'
      const configResponse = await axios({method: 'get', url: `api/config/authentication`})

      if (configResponse.status !== 200) {
        commit('setError', 'Unable to configure authentication')
        return
      }

      const {authentication } = configResponse.data
      commit('setAuthConfig', authentication)
    },

    async login({commit, dispatch, rootState}, payload) {
      commit("setError", null);
      commit("setLoading", true);

      axios.defaults.baseURL = rootState.baseURL;

      try {

        const resLogin = await axios({method: 'post', url: `auth/login`, data: payload});
        if (resLogin.data) {
          const {status, required_2fa, security_question} = resLogin.data;
          if (status === 'valid') {

            // store login response if initial login valid
            commit("setUser", resLogin.data)

            // if 2fa required, show token form if security question is already selected (step 2)
            // otherwise show 2fa setup form, where user chooses their security question (step 1)
            if (required_2fa) {
              await dispatch('sendToken');
              if (security_question) {
                commit("setStep", 2);
              } else {
                await dispatch('getSecurityQuestion');
                commit("setStep", 1);
              }
            } else {
              // go to confirm terms step (step 3)
              commit("setStep", 3);
            }
          } else {
            dispatch('deleteUserJWT')
            commit("setError", "The provided credentials do not match our records.")
          }
        } else {
          dispatch('deleteUserJWT')
          commit('setError', "An error has occurred and we're unable to process your request");
        }
      } catch (error) {
        dispatch('deleteUserJWT')
        commit('setError', error);
        console.log(error)
      } finally {
        commit("setLoading", false);
      }
    },
    async twoFactorSetup({ commit, rootState, getters}, payload) {
      commit("setError", null);
      commit("setLoading", true);

      axios.defaults.baseURL = rootState.baseURL;

      try {
        const resSetup = await axios({
          method: 'post',
          url: `auth/2fa/registration/new`,
          data: payload,
          headers: getters.authorizationHeadersLogin,
          withCredentials: true
        });

        if (resSetup.data.status) {
          commit("setStep", 3);
        } else {
          commit('setError', resSetup.data.error_message);
        }
      } catch (error) {
        commit('setError', error);
        console.log(error)
      } finally {
        commit("setLoading", false);
      }
    },

    // send a token to the user's mobile via SMS
    async sendToken({commit, state, rootState, getters}) {

      axios.defaults.baseURL = rootState.baseURL

      try {
        const resToken = await axios({
          method: 'post',
          url: `auth/2fa/sms/send/${state.user.mobile}`,
          headers: getters.authorizationHeadersLogin,
          withCredentials: true
        });

        if (!resToken.data) {
          commit('setError', "An error has occurred and we're unable to process your request");
        }

      } catch (error) {
        commit('setError', error);
        console.log(error)
      }
    },

    // verify token that was sent via SMS (step 2)
    async verifyToken({ commit, rootState, getters}, payload) {
      commit("setError", null);
      commit("setLoading", true);

      axios.defaults.baseURL = rootState.baseURL;

      try {
        const resToken = await axios({
          method: 'post',
          url: `auth/2fa/token/verify/${payload}`,
          headers: getters.authorizationHeadersLogin,
          withCredentials: true
        });

        if (resToken.data) {
          commit("setStep", 3);
        } else {
          commit('setError', "The provided token do not match.");
        }
      } catch (error) {
        commit('setError', error);
        console.log(error)
      } finally {
        commit("setLoading", false);
      }
    },

    // confirm terms (step 3)
    // if confirmation succeeds, store user JWT
    async confirmAgree({state, dispatch, commit, rootState, getters}) {
      commit("setError", null);
      commit("setLoading", true);

      axios.defaults.baseURL = rootState.baseURL;

      try {
        const resConfirm = await axios({
          method: 'post',
          url: `auth/2fa/confirm`,
          headers: getters.authorizationHeadersLogin,
          withCredentials: true
        });

        if (!resConfirm.data) {
          dispatch('deleteUserJWT')
          commit('setError', "The provided token do not match.");
        }

        // if agreed, set user, if error getting access token create specific error
       state?.user?.access_token ?
          dispatch('storeUserJWT', state.user.access_token)
         : commit('setError', "Unable to access user token")

      } catch (error) {
        dispatch('deleteUserJWT')
        commit('setError', error);
        console.log(error)
      } finally {
        commit("setLoading", false);
      }
    },
    async getSecurityQuestion({commit, getters, rootState}) {
      axios.defaults.baseURL = rootState.baseURL;

      try {
        const resQuestion = await axios({
          method: 'get',
          url: `auth/2fa/security_questions`,
          headers: getters.authorizationHeadersLogin,
          withCredentials: true
        });

        if (resQuestion.data) {
          commit('setQuestions', resQuestion.data);
        } else {
          commit('setError', "An error has occurred and we're unable to process your request");
        }
      } catch (error) {
        commit('setError', error);
        console.log(error)
      }
    },
    cancelLogIn({commit, dispatch}) {
      dispatch('deleteUserJWT')
      commit("setError", null);
      commit("setUser", null);
      commit("setStep", 0);
    },
    forgotPhone({commit}) {
      commit("setError", null);
      commit("setStep", 4);
    },
    async changePhone({getters, commit, rootState}, payload) {
      commit("setError", null);
      commit("setLoading", true);

      axios.defaults.baseURL = rootState.baseURL;

      try {
        const resChange = await axios({
          method: 'post',
          url: `auth/2fa/update/mobileno`,
          data: payload,
          headers: getters.authorizationHeadersLogin,
          withCredentials: true
        });

        if (resChange.data.status) {
          commit("setStep", 2);
        } else {
          commit('setError', resChange.data.error_message);
        }
      } catch (error) {
        commit('setError', error);
        console.log(error)
      } finally {
        commit("setLoading", false);
      }
    },
    forgotPassword({commit}) {
      commit("setError", null);
      commit("setStep", 5);
    },
    async requestForgotPassword({commit, rootState}, payload) {
      commit("setError", null);
      commit("setLoading", true);

      axios.defaults.baseURL = rootState.baseURL;

      try {
        const resForgot = await axios({
          method: 'post',
          url: `auth/fp/email/send`,
          data: payload
        });

        if (resForgot.data.status) {
          commit("setStep", 6);
        } else {
          commit('setError', resForgot.data.error_message);
        }
      } catch (error) {
        commit('setError', error);
        console.log(error)
      } finally {
        commit("setLoading", false);
      }
    },
    async getUserReset({commit, rootState}, payload) {
      commit("setError", null);
      commit("setLoading", true);

      axios.defaults.baseURL = rootState.baseURL;

      try {
        const resReset = await axios({
          method: 'post',
          url: `auth/fp/2fa/sms/send`,
          data: payload
        });

        commit("setReset", resReset.data);
        if (resReset.data.error_message !== '') {
          commit('setError', resReset.data.error_message);
        }
      } catch (error) {
        commit('setError', error);
        console.log(error)
      } finally {
        commit("setLoading", false);
      }
    },
    async resetPassword({commit, rootState}, payload) {
      commit("setError", null);
      commit("setLoading", true);
      commit("setResetSuccess", false);

      axios.defaults.baseURL = rootState.baseURL;

      try {
        const resReset = await axios({
          method: 'post',
          url: `auth/fp/reset`,
          data: payload
        });

        commit("setResetSuccess", resReset.data.status);

        if (resReset.data.error_message !== '') {
          commit('setError', resReset.data.error_message);
        }
      } catch (error) {
        commit('setError', error);
        console.log(error)
      } finally {
        commit("setLoading", false);
      }
    },

    /**
     * store JWT token in cookie and local state
     * @param [state]
     * @param [commit]
     * @param token
     * @returns {void}
     */
    storeUserJWT({state, commit}, token) {

      // store full token in local state
      commit('setBearerToken', token)

      // decode token to get expiry and user object (payload)
      const decodedToken = jwt.decode(token, {complete: true})

      // store token in cookie
      const expires = new Date(decodedToken.payload.exp * 1000).toUTCString()
      Vue.$cookies.set(state.userTokenStorageKey, token, expires)
    },

    /**
     * remove JWT token
     * null user object and bearer token in local state
     *
     * // todo: redirect, or just assume non-logged-in user?
     *
     * @param [state]
     * @param [commit]
     * @returns {void}
     */
    deleteUserJWT({state, commit}) {
      commit('setUser', null)
      commit('setBearerToken', null)
      Vue.$cookies.remove(state.userTokenStorageKey)
    },


    /**
     * Check for url params and set them in state,
     * if found, try to set event user with one click
     * if not found, try to set user from cookie
     *
     * @param {Function} [dispatch]
     * @param {Function} [commit]
     *
     * @returns {Promise<boolean>} event user is set or not
     */
    async initiateAuthAndSetEventUser({dispatch, commit}) {

      // parse url params
      const params = new URLSearchParams(window.location.search)
      // convert to lowercase
      const paramsLowercase = {}
      for (const [key, value] of params) {
        paramsLowercase[key.toLowerCase()] = value
      }

      // store all params
      commit('setUrlParams', paramsLowercase)

      // try to set event user with one-click
      const ck = paramsLowercase.ck || null,
        id = paramsLowercase.id || null

      let eventUserSet = false

      if (ck === 'x' || id === 'x') {
        // don't set event user and return successful init.
        return true
      }

      if (ck && id) {
        eventUserSet = await dispatch('setEventUserWithOneClick', {id, ck})
      }

      // try to set logged-in user from cookie
      // if eventUser not already set by one-click, then set logged-in user as event user via authenticatedclick
      eventUserSet = await dispatch('setUserFromCookie')

      return eventUserSet;

    },

    /**
     * Verify user JWT and set Event User if successful (if not already set by 1click)
     *
     * @param {Object} [rootState]
     * @param {Object} [state]
     * @param {Function} [commit]
     * @param {String} payload JWT
     *
     * @returns {Promise<boolean>} whether logged-in user has been verified and set
     */
    async verifyJWTAndSetEventUser({rootState, state, commit}, payload) {
      try {
        const authenticatedClickResponse = await axios({
          method: 'post',
          url: `registration/authenticatedclick`,
          baseURL: rootState.baseURL,
          headers: {'Authorization': `Bearer ${payload}`},
        })
        if (authenticatedClickResponse.status !== 200) {
          return false
        }

        // if event user not already set by 1click, set logged-in user as event user
        // store user info returned by JWT verification call (i.e. event user info)
        if (!state.eventUser) {
          commit('setEventUser', authenticatedClickResponse.data || null)
          if (state.eventUser) {
            commit('setEventUserSetBy', 'authenticatedClick')
          }
        }

        return true

      } catch (error) {
        throw new Error('Could not verify JWT', {cause: error})
      }
    },


    /**
     * Set Event User from URL params
     * @param [commit]
     * @param [rootState]
     * @param {Object} payload {id, ck}
     *  @param {String} payload.id
     *  @param {String} payload.ck
     * @returns {Promise<boolean>}
     */
    async setEventUserWithOneClick({commit, rootState}, payload) {
      try {

        axios.defaults.method = 'POST'

        const oneClickResponse = await axios({
          baseURL: rootState.baseURL,
          url: `registration/${payload.id}/oneclick`,
          data: {
            'one_click_token': payload.ck,
            "one_click_module": "CONTACT-UPDATE"
          }
        })
        if (oneClickResponse.status === 200) {
          const eventUser = oneClickResponse.data || null
          commit('setEventUser', eventUser)
          commit('setEventUserSetBy', 'oneClick')
          return !!eventUser
        }

        // todo: handle other responses?
        return false

      } catch (error) {
        throw new Error('Could not verify contact detail', {cause: error})
      }

    },


    /**
     * Attempt to set Logged-in User from stored cookie
     * @param {Object} [state]
     * @param {Object} [commit]
     * @param {Function} [dispatch]
     *
     * @returns {Boolean} Whether user successfully set from cookie
     * */
    async setUserFromCookie({state, commit, dispatch}) {

      try {

        // check if key exists
        if (!Vue.$cookies.isKey(state.userTokenStorageKey)) {
          return false
        }

        const token = Vue.$cookies.get(state.userTokenStorageKey)

        // check if token is expired
        const decodedToken = jwt.decode(token, {complete: true})
        const userJWT = decodedToken.payload
        if (userJWT.exp < Date.now() / 1000) {
          // console.log('token expired')
          dispatch('deleteUserJWT')
          return false
        }

        // verify token before storing any data, will set event user if valid
        const verifiedResponse = await dispatch('verifyJWTAndSetEventUser', token)
        if (!verifiedResponse) {
          return false
        }

        // store token in state
        commit('setBearerToken', token)

        return true

      } catch (error) {
        throw new Error('Failed to set user from cookie', {cause: error})
      }
    }
  },
  getters: {
    authorizationHeadersLogin(state) {
      const token = state?.user?.access_token || ''
      return {Authorization: `Bearer ${token}`}
    },
    authorizationHeadersStored(state) {
      if (!state.bearerToken) {
        return null
      }
      return {Authorization: `Bearer ${state.bearerToken}`}
    },
    loggedInUser(state) {
      if (!state.bearerToken) {
        return null
      }
      const decodedToken = jwt.decode(state.bearerToken, {complete: true})
      return decodedToken.payload
    },
    userIsStaff(state, getters) {
      const staffRoles = ["CHQStaff", "LPANSWStaff"]
      const roleValidation = (role) => staffRoles.includes(role);
      return getters.loggedInUser && getters.loggedInUser?.Role.some(roleValidation);
    },


    /**
     * Check if user has RGTelemarketer role
     * @param state
     * @param getters
     * @return {boolean}
     */
    userIsTelemarketer(state, getters) {

      // check logged-in user and get role
      let userRole = getters.loggedInUser?.Role || false

      // if no user role found, return false
      if (!getters.loggedInUser?.Role) return false

      // if userRole is string, convert to array
      userRole = Array.isArray(userRole) ? userRole : [userRole]

      // return whether user has RGTelemarketer role
      return userRole.includes('RGTelemarketer')

    },

    /**
     * Check if user has staff payment types
     * @param state
     * @param getters
     * @returns {Boolean}
     */
    userHasStaffPaymentTypes(state, getters) {

      // first check for logged-in user, if not logged in, try getting token from URL params
      let userRole = getters.loggedInUser?.Role || false
      let usingUrlToken = false

      // if (!userRole) {
      // try getting token from URL params
      // cannot assume that they are already set via auth, this is for when accessing in Portal
      const params = new URLSearchParams(window.location.search)
      const token = params.get('token')
      if (token) {
        const decodedToken = jwt.decode(token, {complete: true})
        userRole = decodedToken.payload?.Role || false
        usingUrlToken = true
      }
      // }

      // if no user role found, return false
      if (!userRole) return false

      // if userRole is string, cast to array
      if (typeof userRole === 'string') userRole = [userRole]

      // if roles found in either token, return whether or not it matches with staff roles allowing payment options
      // note: leave as array, as user may have multiple staff payment types down the track
      const staffRoles = usingUrlToken ?
        ["AdminPortal", "CHQStaff", "OCERAccess"]
        : ["LPANSWStaff", "CHQStaff"]

      const roleValidation = (role) => staffRoles.includes(role);
      return userRole.some(roleValidation);
    }
  },
};
